/**
 * A Download button that opens a list of formats.
 *
 * This exists because three surfaces — the chat artifact pane, the file preview
 * and the conversation transcript — each offered exactly one export, and each
 * would otherwise have grown its own dropdown. It knows nothing about documents:
 * every surface supplies its own items, which is what keeps "a .docx makes no
 * sense for a PDF" a decision at the call site rather than a branch in here.
 *
 * It is also the codebase's first *accessible* dropdown. The existing hand-rolled
 * ones (DocumentationPage, UserPicker) are mouse-only, with no `aria-expanded`,
 * no `role="menu"` and no keyboard roaming. Something reused three times should
 * not repeat that, so the roving focus and Escape handling live here once.
 *
 * Items come in two shapes. A `href` item is an ordinary link and downloads
 * instantly. A `run` item is asynchronous — saving a draft, rendering a .docx,
 * uploading to Drive — and narrates itself through `setStatus` while it works;
 * throwing from it keeps the menu open and shows the message, because "the
 * export did not happen" is exactly what a closing menu would hide.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import './ExportMenuComponent.css';

export interface ExportMenuItem {
  id: string;
  label: string;
  /** Second line — why it is disabled, or what it will do first. */
  hint?: string;
  disabled?: boolean;
  /** Link item: downloads immediately, closes the menu. */
  href?: string;
  download?: string;
  /** Action item: `setStatus` narrates progress; throwing surfaces the message. */
  run?: (setStatus: (status: string) => void) => Promise<void>;
}

export interface ExportMenuComponentProps {
  items: ExportMenuItem[];
  /** Trigger text. Defaults to "Download". */
  label?: string;
  /** Optional glyph before the label, so a surface can keep its own icon. */
  icon?: React.ReactNode;
  /** Extra classes on the trigger, so each surface keeps its own button skin. */
  triggerClassName?: string;
  disabled?: boolean;
  /** Which edge the panel hangs from. Defaults to the right. */
  align?: 'left' | 'right';
  ariaLabel?: string;
}

export function ExportMenuComponent(props: ExportMenuComponentProps): React.ReactElement {
  const { items, label = 'Download', align = 'right', disabled = false } = props;

  const [open, setOpen] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  /** An item is still working; closing now would strand the user mid-export. */
  const busy = runningId !== null;

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setError(null);
    setStatus(null);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // No focus return: the user clicked somewhere else on purpose.
        setOpen(false);
        setError(null);
        setStatus(null);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  /**
   * Focusable items, read from the DOM rather than collected into a ref array.
   * The array approach needs the ref callbacks and the render order to stay in
   * step through every disable/enable, and they do not.
   */
  const focusableItems = (): HTMLElement[] =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);

  const focusItem = useCallback((index: number) => {
    const focusable = focusableItems();
    if (focusable.length === 0) return;
    const wrapped = ((index % focusable.length) + focusable.length) % focusable.length;
    focusable[wrapped]?.focus();
  }, []);

  const openAndFocus = useCallback((index: number) => {
    setOpen(true);
    // After paint — the items do not exist yet to receive focus.
    requestAnimationFrame(() => focusItem(index));
  }, [focusItem]);

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open) focusItem(0);
      else openAndFocus(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAndFocus(-1);
    }
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const focusable = focusableItems();
    const current = focusable.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        if (!busy) close(true);
        break;
      case 'ArrowDown':
        event.preventDefault();
        focusItem(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(-1);
        break;
      case 'Tab':
        if (!busy) close(false);
        break;
      default:
        break;
    }
  };

  const runItem = async (item: ExportMenuItem) => {
    if (!item.run || busy) return;
    setRunningId(item.id);
    setStatus(null);
    setError(null);
    try {
      await item.run(setStatus);
      setRunningId(null);
      close(true);
    } catch (caught) {
      setRunningId(null);
      setStatus(null);
      setError(caught instanceof Error ? caught.message : 'That export did not work.');
    }
  };

  return (
    <div className={`export-menu${busy ? ' export-menu--busy' : ''}`} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`export-menu-trigger${props.triggerClassName ? ` ${props.triggerClassName}` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={props.ariaLabel}
        disabled={disabled}
        onClick={() => { if (!open) setOpen(true); else if (!busy) close(false); }}
        onKeyDown={onTriggerKeyDown}
      >
        {props.icon}
        <span className="export-menu-trigger-label">{label}</span>
        <span className="export-menu-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          className={`export-menu-list${align === 'left' ? ' export-menu-list--left' : ''}`}
          id={menuId}
          ref={menuRef}
          role="menu"
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item) => {
            const itemDisabled = Boolean(item.disabled) || (busy && runningId !== item.id);
            const running = runningId === item.id;
            const hint = running && status ? status : item.hint;

            const body = (
              <>
                <span className="export-menu-item-label">{item.label}</span>
                {hint && <span className="export-menu-item-hint">{hint}</span>}
              </>
            );

            if (item.href && !itemDisabled) {
              return (
                <a
                  key={item.id}
                  role="menuitem"
                  className="export-menu-item"
                  href={item.href}
                  download={item.download}
                  onClick={() => close(false)}
                >
                  {body}
                </a>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={`export-menu-item${running ? ' export-menu-item--running' : ''}`}
                disabled={itemDisabled}
                onClick={() => { void runItem(item); }}
              >
                {body}
              </button>
            );
          })}

          {busy && <p className="export-menu-status" role="status">{status ?? 'Working…'}</p>}
          {error && <p className="export-menu-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
